import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { getEnvTenantId, normalizeTenantId } from "@/lib/tenantId";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalTenantId,
} from "@/lib/storeManager";
import {
  SD_ROLE_STORAGE_KEY,
  rebindSecureRole,
  removeSecureLocalItem,
  verifySecureAuthStorage,
} from "@/lib/secureLocalStorage";

export { SD_ROLE_STORAGE_KEY, verifySecureAuthStorage } from "@/lib/secureLocalStorage";

export const SD_AUTH_CHANGED_EVENT = "sd-auth-changed";

export function notifySdAuthChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SD_AUTH_CHANGED_EVENT));
  }
}

/** Valid, non-expired JWT in localStorage */
export function isValidAccessToken(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (typeof decoded.exp !== "number") return true;
    return decoded.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function hasRefreshToken(): boolean {
  return Boolean(getRefreshToken());
}

type AuthBody = Record<string, unknown>;

/** Supports tokens at root, under `data`, or under `tokens` / `data.tokens`. */
export function extractAuthTokens(body: unknown): {
  access: string | null;
  refresh: string | null;
} {
  const root = (body ?? {}) as AuthBody;
  const data = (root.data ?? null) as AuthBody | null;
  const rootTokens = (root.tokens ?? null) as AuthBody | null;
  const dataTokens = (data?.tokens ?? null) as AuthBody | null;

  const accessRaw =
    rootTokens?.access ??
    dataTokens?.access ??
    data?.access ??
    root.access;

  const refreshRaw =
    rootTokens?.refresh ??
    dataTokens?.refresh ??
    data?.refresh ??
    root.refresh;

  return {
    access: typeof accessRaw === "string" ? accessRaw : null,
    refresh: typeof refreshRaw === "string" ? refreshRaw : null,
  };
}

export function extractAuthUser(body: unknown): unknown {
  const root = (body ?? {}) as AuthBody;
  const data = (root.data ?? null) as AuthBody | null;
  return data?.user ?? root.user;
}

export function persistAuthTokens(body: unknown): {
  access: string | null;
  refresh: string | null;
} {
  const cachedRole =
    typeof window !== "undefined"
      ? localStorage.getItem(SD_ROLE_STORAGE_KEY)
      : null;

  const { access, refresh } = extractAuthTokens(body);
  if (access) setLocalAccessToken(access);
  if (refresh) setLocalRefreshToken(refresh);

  if (cachedRole) {
    rebindSecureRole(cachedRole);
  }

  return { access, refresh };
}

/**
 * POST /auth/token-refresh/ with JSON body `{ refresh }` and X-Tenant-Id.
 */
export async function requestTokenRefresh(
  refreshTokenValue: string
): Promise<{ access: string | null; refresh: string | null } | null> {
  try {
    const baseURL = (import.meta.env.VITE_API_BASE_URL as string).replace(
      /\/$/,
      ""
    );
    const tenantId = getEnvTenantId() || normalizeTenantId(getTenantId()) || "";
    const response = await axios.post(
      `${baseURL}/auth/token-refresh/`,
      { refresh: refreshTokenValue },
      {
        headers: {
          "Content-Type": "application/json",
          ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
        },
      }
    );
    return extractAuthTokens(response.data);
  } catch {
    return null;
  }
}

/**
 * Refresh access (and optional rotate refresh) without logging out.
 * Shared in-flight promise prevents parallel refresh calls from invalidating tokens.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function performTokenRefresh(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;

  const cachedRole =
    typeof window !== "undefined"
      ? localStorage.getItem(SD_ROLE_STORAGE_KEY)
      : null;

  const tokens = await requestTokenRefresh(refresh);
  if (!tokens?.access) return false;

  setLocalAccessToken(tokens.access);
  if (tokens.refresh) setLocalRefreshToken(tokens.refresh);

  if (cachedRole) {
    rebindSecureRole(cachedRole);
  }

  notifySdAuthChanged();
  return isValidAccessToken();
}

export async function tryRefreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = performTokenRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/** Clear SD session but keep tenant id for login */
export function clearSdAuthSession(): void {
  removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
  const tenant = getTenantId();
  clearLocalStorage();
  if (tenant) setLocalTenantId(tenant);
}

/** Detect manual localStorage edits and force re-login. */
export function assertSecureAuthStorage(): boolean {
  if (!verifySecureAuthStorage()) {
    clearSdAuthSession();
    return false;
  }
  return true;
}

export function redirectToSdLogin(): void {
  if (!window.location.pathname.startsWith("/sd/login")) {
    window.location.replace("/sd/login");
  }
}
